import { MouseEvent, useState } from "react";
import { createUseStyles } from "react-jss";
import {
  isRouteErrorResponse,
  useLocation,
  useParams,
  useRouteError,
} from "react-router-dom";
import StrokeDisplay from "../components/StrokeDisplay";
import DrawnTrace from "../models/DrawnTrace";
import { chooseLanguage } from "../utils/chooseLanguage";
import extractStrokesFromSVG, { SIZE } from "../utils/extractStrokesFromSVG";
import verifyStrokes from "../utils/verifyStrokes";

type State = "idle" | "drawing" | "correct" | "incorrect";
function isStateVerified(state: State): state is "correct" | "incorrect" {
  return state === "correct" || state === "incorrect";
}
export default function CharacterRoute() {
  const [state, setState] = useState<State>("idle");
  const [message, setMessage] = useState<string>("");
  const [score, setScore] = useState<number>(0);
  const [drawnTraces, setDrawnTraces] = useState<DrawnTrace[]>([]);
  const [isReferenceShown, setIsReferenceShown] = useState(true);
  const [isRedraw, setIsRedraw] = useState(false);
  const [strokeSpeed, setStrokeSpeed] = useState(0.5);
  const classes = useStyles({ state });

  const location = useLocation();
  const currentRoute =
    location.state?.currentRoute ?? location.pathname.split("/")[1];
  const language = chooseLanguage(currentRoute);

  const { character } = useParams();
  if (character === undefined || !(character in language)) {
    throw new Response("Not Found", { status: 404 });
  }

  const referenceStrokes = extractStrokesFromSVG(
    language[character as keyof typeof language]
  );

  const displayCharacter = () => {
    if (isReferenceShown && !isRedraw) {
      return referenceStrokes.map((stroke, index) => (
        <StrokeDisplay
          key={index}
          index={index}
          stroke={stroke}
          strokeSpeed={strokeSpeed}
        />
      ));
    }
    return null;
  };

  const redraw = () => {
    setIsRedraw(true);
    setTimeout(() => setIsRedraw(false), 0.1);
  };

  return (
    <div className={classes.quiz}>
      {message && score ? (
        <div className={classes.helperText}>
          {message + `. Match Score ${Math.round(score)}%`}
        </div>
      ) : null}
      {message && !score ? (
        <div className={classes.helperText}>
          {message}
        </div>
      ) : null}
      <svg
        key={character} // Remount (and therefore restart the animation) when the active character changes.
        width={SIZE * 3}
        height={SIZE * 3}
        viewBox={`0 0 ${SIZE} ${SIZE}`}
        onPointerDown={(event) => {
          // TODO: What if setPointerCapture fails?
          event.currentTarget.setPointerCapture(event.pointerId);
          setState("drawing");
          setDrawnTraces((traces) => [...traces, []]);
        }}
        onLostPointerCapture={() => setState("idle")}
        onMouseMove={(event) => {
          if (state === "drawing") {
            addPoint(event);
          }
        }}
        className={classes.display}
      >
        <path
          d={
            // Outline
            `M 0 0 H ${SIZE} V ${SIZE} H 0 Z` +
            // Middle Lines
            `M 0 ${SIZE / 2} H ${SIZE}` +
            `M ${SIZE / 2} 0 V ${SIZE}` +
            // Diagonals
            `M 0 0 ${SIZE} ${SIZE}` +
            `M ${SIZE} 0 0 ${SIZE}`
          }
          fill="none"
          stroke="black"
          opacity={0.1}
          strokeWidth={1}
        />
        {displayCharacter()}
        {drawnTraces.map((trace, index) =>
          trace.length > 1 ? (
            <path
              key={index}
              d={`M${trace.map(({ x, y }) => `${x},${y}`).join("L")}`}
              fill="none"
              stroke="red"
              strokeWidth={2}
            />
          ) : null
        )}
      </svg>
      <form
        className={classes.controls}
        onSubmit={(event) => {
          event.preventDefault();
          // TODO: releasePointerCapture? might not be necessary
          const data = verifyStrokes(referenceStrokes, drawnTraces);
          setState(data?.correct ? "correct" : "incorrect");
          setMessage(data?.message);
          setScore(data?.score ?? 0);
        }}
      >
        <button type="submit" disabled={drawnTraces.length === 0}>
          Verify
        </button>
        <button
          type="button"
          disabled={drawnTraces.length === 0}
          onClick={() => {
            setState("idle"); // TODO: releasePointerCapture? might not be necessary
            setDrawnTraces([]);
            setMessage("");
            setScore(0);
          }}
        >
          Clear
        </button>
        <div className={classes.spacer} />
        <button
          type="button"
          onClick={() => setIsReferenceShown((isShown) => !isShown)}
        >
          {isReferenceShown ? "Hide" : "Show"}
        </button>
      </form>
      <div className={classes.animationControl}>
        <button
          type="button"
          className={
            strokeSpeed === 2.5
              ? classes.animationSelectedControlButton
              : classes.animationControl
          }
          onClick={() => setStrokeSpeed(2.5)}
        >
          Slow
        </button>
        <button
          type="button"
          className={
            strokeSpeed === 1.5
              ? classes.animationSelectedControlButton
              : classes.animationControl
          }
          onClick={() => setStrokeSpeed(1.5)}
        >
          Medium
        </button>
        <button
          type="button"
          className={
            strokeSpeed === 0.5
              ? classes.animationSelectedControlButton
              : classes.animationControl
          }
          onClick={() => setStrokeSpeed(0.5)}
        >
          Fast
        </button>
      </div>

      <button className={classes.redrawButton} type="button" onClick={redraw}>
        redraw
      </button>
    </div>
  );

  function addPoint(event: MouseEvent<SVGSVGElement, globalThis.MouseEvent>) {
    const screenPoint = event.currentTarget.createSVGPoint();
    screenPoint.x = event.clientX;
    screenPoint.y = event.clientY;

    const svgPoint = screenPoint.matrixTransform(
      event.currentTarget.getScreenCTM()!.inverse()
    );
    const { x, y } = svgPoint;
    setDrawnTraces((traces) => [
      ...traces.slice(0, -1),
      [...traces[traces.length - 1], { x, y }],
    ]);
  }
}
interface StylesData {
  state: State;
}
const useStyles = createUseStyles({
  quiz: {
    marginInline: "auto",
    width: "fit-content",
    display: "flex",
    flexDirection: "column",
    gap: "0.5rem",
    alignItems: "center",
  },
  display: {
    overflow: "visible",
    backgroundColor: ({ state }: StylesData) => {
      if (state === "idle") {
        return "white";
      } else if (state === "drawing") {
        return "#eee";
      } else if (state === "correct") {
        return "lightgreen";
      } else if (state === "incorrect") {
        return "pink";
      } else {
        assertUnreachable(state);
      }
    },
    pointerEvents: ({ state }: StylesData) =>
      isStateVerified(state) ? "none" : "initial",
  },
  controls: {
    "display": "flex",
    "gap": "0.25em",
    '& button[type="submit"]': {
      fontWeight: "bold",
    },
  },
  spacer: {
    flexGrow: 1,
  },
  helperText: {
    color: "red",
  },
  animationControl: {
    display: "flex",
    flexDirection: "row",
    gap: 2,
  },
  animationControlButton: {
    backgroundColor: "white",
    borderColor: "white",
  },
  animationSelectedControlButton: {
    backgroundColor: "lightgray",
    borderColor: "lightgray",
  },
  redrawButton: {
    width: "80%",
  },
});

/******************************************************************************/

function assertUnreachable(_value: never): never {
  throw new Error("Statement should be unreachable");
}

// Based on https://github.com/remix-run/react-router/blob/8e09bd601bd7f3066ff598ddd8e23b9b7cc462b3/packages/react-router/lib/hooks.tsx
export function CharacterErrorBoundary() {
  let error = useRouteError();
  let message = isRouteErrorResponse(error)
    ? `${error.status} ${error.statusText}`
    : error instanceof Error
    ? error.message
    : JSON.stringify(error);
  let stack =
    error instanceof Error && process.env.NODE_ENV === "development"
      ? error.stack
      : null;
  let lightgrey = "rgba(200,200,200, 0.5)";
  let preStyles = { padding: "0.5rem", backgroundColor: lightgrey };
  return (
    <>
      <h2>Oops!</h2>
      <p>Sorry, an unexpected error has occured.</p>
      <p style={{ opacity: 0.5, fontStyle: "italic" }}>
        {message.substring(0, 1).toUpperCase() + message.substring(1)}
      </p>
      {stack ? <pre style={preStyles}>{stack}</pre> : null}
    </>
  );
}
